import React from "react";
import Modal from "./Modal";
import MuseColorLogoDuo from "../Logos/MuseColorLogoDuo";
import { TrackingObject } from "../../types/TrackingTypes";
import { NewsletterForm } from "./NewsletterForm";
import styles from "./NewsletterModal.module.scss";

interface NewsletterModalProps {
    isOpen: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onFormSubmit: (formData: object) => Promise<boolean>;
    impressionTrackingProps?: TrackingObject;
    submitTrackingProps?: TrackingObject;
    closeTrackingProps?: TrackingObject;
    setIsOpen: (x: boolean) => void;
    newsletterRef?: (node: HTMLDivElement) => void;
}

/**
 * Generate a Newsletter modal. This modal does not accept children.
 * @param {Function} props.onClose - A function to fire when the modal becomes closed.
 * @param {Function} props.onFormSubmit - A function to fire when the form is submitted.
 * @param {Boolean} props.isOpen - Is the modal currently open?
 * @param {Function} props.setIsOpen - A function to set the stat of the modal from outside.
 * @param {TrackingObject} props.submitTrackingProps - An optional submit tracking object.
 * @param {TrackingObject} props.closeTrackingProps - An optional close tracking object.
 * @param {TrackingObject} props.impressionTrackingProps - An optional impression tracking object.
 * @param {Function} props.newsletterRef - An optional callback ref.
 * @example <Modal {...args} />
 * @returns An ADA compliant modal as a [React Portal](https://reactjs.org/docs/portals.html).
 */

const NewsletterModal = (props: NewsletterModalProps) => {
    const {
        onClose,
        onFormSubmit,
        impressionTrackingProps,
        submitTrackingProps,
        closeTrackingProps,
        isOpen,
        setIsOpen,
        newsletterRef
    } = props;

    return (
        <Modal
            onClose={onClose}
            containerClassName=""
            contentClassName={styles.content}
            closeButtonClassName={styles.newsletterCloseButton}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            label = "Newsletter sign up form"
            {...{impressionTrackingProps, closeTrackingProps}}
        >
            <div
                className={styles.container}
                data-tracking-props={JSON.stringify(impressionTrackingProps)}
                data-tracking-event-type="impression"
                ref={newsletterRef}
            >
                <img
                    src="https://cms-assets.themuse.com/media/woman_searching_for_jobs.jpg"
                    loading="lazy"
                    alt="Woman working at desk"
                    className={styles.image}
                    width={350}
                    height={500}
                />
                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.logo}>
                            <MuseColorLogoDuo />
                        </div>
                        <div className={styles.header}>
                            #1 CAREER TIP: SIGN UP FOR EMAILS
                        </div>
                        <div className={styles.body}>
                            We&#39;ll send you emails with advice, tips, and trends, plus company insights
                            and job opportunities.
                        </div>
                        <NewsletterForm
                            onFormSubmit={onFormSubmit}
                            submitTrackingProps={submitTrackingProps}
                        />
                        <span className={styles.tos}>
                            {"By entering your email, you agree to our "}
                            <a href="/user/terms" target="_blank">Terms of Service</a>
                            {" & "}
                            <a href="/user/privacy" target="_blank">Privacy Policy</a>
                            .
                        </span>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export { NewsletterModal as default };
export type { NewsletterModalProps };
